import { useCallback, useEffect, useMemo } from "react";
import {
  useDeleteSubmissionPeriodMutation,
  useUpdateSubmissionPeriodMutation,
} from "src/services";
import { isApiError } from "src/utils/apiError";
import { toastError, toastSuccess } from "src/utils/toast";
import { Values } from "./validation";

export const useDeleteSubmissionPeriod: (onClose: () => Promise<void>) => {
  errors: string[] | undefined;
  onDelete: (submissionPeriodId: number) => Promise<void>;
} = (onClose) => {
  const [
    trigger,
    { data, error, isError, isLoading, isSuccess, isUninitialized },
  ] = useDeleteSubmissionPeriodMutation();
  useEffect(() => {
    if (!isUninitialized && !isLoading && isSuccess) {
      toastSuccess("Submission Period has been deleted");
    }
  }, [data, isError, isLoading, isSuccess, isUninitialized]);

  const onDelete = useCallback(
    async (submissionPeriodId: number) => {
      try {
        await trigger({ submissionPeriodId }).unwrap();
        await onClose();
      } catch {
        toastError(
          "An error occurred. Submission Period could not be deleted."
        );
      }
    },
    [trigger]
  );

  const errors = useMemo(() => {
    if (!error || !isError) {
      return undefined;
    }

    if (isApiError(error)) {
      return [error.data?.error];
    }

    return ["Please try again or contact support@120water.com"];
  }, [error, isError]);

  return useMemo(() => ({ errors, onDelete }), [errors, onDelete]);
};

export const useUpdateSubmissionPeriod: (onClose: () => Promise<void>) => {
  errors: string[] | undefined;
  onSubmit: (values: Values, submissionPeriodId: number) => Promise<void>;
} = (onClose) => {
  const [
    trigger,
    { data, error, isError, isLoading, isSuccess, isUninitialized },
  ] = useUpdateSubmissionPeriodMutation();
  useEffect(() => {
    if (!isUninitialized && !isLoading && isSuccess) {
      toastSuccess("Submission Period has been updated");
    }
  }, [data, isError, isLoading, isSuccess, isUninitialized]);

  const onSubmit = useCallback(
    async (values: Values, submissionPeriodId: number) => {
      const {
        name,
        submissionType,
        dueDate,
        lockedDate,
        overdueSetting,
        active,
      } = values;

      const submissionPeriod = {
        lockedDate: lockedDate ? new Date(lockedDate).toISOString() : null,
        dueDate: new Date(dueDate).toISOString(),
        active,
        name,
        submissionType,
        overdueSetting,
      };

      try {
        await trigger({ submissionPeriod, submissionPeriodId }).unwrap();
        await onClose();
      } catch (error) {
        if (error !== null && typeof error === "object" && "data" in error) {
          const data = error.data;
          if (
            data !== null &&
            typeof data === "object" &&
            "consumerErrorMessage" in data
          ) {
            const errorMessage = data.consumerErrorMessage as string;
            if (errorMessage.includes("BadRequestException")) {
              toastError(errorMessage.replace("BadRequestException: ", ""));
              return;
            }
          }
        }
        toastError(
          "An error occurred. Submission Period could not be updated."
        );
      }
    },
    [trigger]
  );

  const errors = useMemo(() => {
    if (!error || !isError) {
      return undefined;
    }

    if (isApiError(error)) {
      return [error.data?.error];
    }

    return ["Please try again or contact support@120water.com"];
  }, [error, isError]);

  return useMemo(() => ({ errors, onSubmit }), [errors, onSubmit]);
};
